import { DepositBackendResponse, DepositRuleBackendResponse, DepositRuleTypeEnum } from "../modules/shared/model";

export interface ApplicableDepositRule {
	rule: DepositRuleBackendResponse;
	deposit: DepositBackendResponse;
	depositAmount: number;
	remainingAmount: number;
}

/**
 * Parse threshold string to extract numeric value and comparison operator
 * Examples:
 * - "Orders above 3000" -> { value: 3000, operator: "above" }
 * - "Orders under $500" -> { value: 500, operator: "under" }
 * - "Orders exceeds 4000" -> { value: 4000, operator: "exceeds" }
 * - "3000" -> { value: 3000, operator: "above" }
 */
export const parseThreshold = (threshold: string): { value: number; operator: string } => {
	// Remove common currency symbols and clean the string
	const cleanedThreshold = threshold
		.toLowerCase()
		.replace(/[$€£¥,]/g, "")
		.trim();

	// Try to extract numeric value
	const numericMatch = cleanedThreshold.match(/\d+\.?\d*/);
	const value = numericMatch ? parseFloat(numericMatch[0]) : 0;

	// Determine operator based on keywords
	let operator = "above"; // default
	if (cleanedThreshold.includes("under") || cleanedThreshold.includes("below") || cleanedThreshold.includes("less than")) {
		operator = "under";
	} else if (cleanedThreshold.includes("above") || cleanedThreshold.includes("exceeds") || cleanedThreshold.includes("greater than") || cleanedThreshold.includes("over")) {
		operator = "above";
	} else if (cleanedThreshold.includes("equals") || cleanedThreshold.includes("exactly")) {
		operator = "equals";
	}

	return { value, operator };
};

/**
 * Check if an amount matches a threshold condition
 */
export const matchesThreshold = (amount: number, threshold: string): boolean => {
	const { value, operator } = parseThreshold(threshold);

	switch (operator) {
		case "above":
		case "exceeds":
		case "greater than":
			return amount > value;
		case "under":
		case "below":
		case "less than":
			return amount < value;
		case "equals":
		case "exactly":
			return amount === value;
		default:
			return amount > value;
	}
};

/**
 * Calculate deposit amount based on rule type
 */
export const calculateDepositAmount = (
	totalAmount: number,
	rule: DepositRuleBackendResponse
): number => {
	const ruleTypeId = parseInt(rule.type.id);

	if (ruleTypeId === DepositRuleTypeEnum.Percentage) {
		// Percentage-based deposit
		const percentage = rule.amount;
		return (totalAmount * percentage) / 100;
	} else if (ruleTypeId === DepositRuleTypeEnum.Fixed_Amount) {
		// Fixed amount deposit
		return rule.amount;
	}

	return 0;
};

/**
 * Find the most applicable deposit rule for a given amount
 * Returns the rule with the highest threshold that still matches
 */
export const findApplicableDepositRule = (
	totalAmount: number,
	deposits: DepositBackendResponse[],
	userId?: string
): ApplicableDepositRule | null => {
	// Filter active deposits
	const activeDeposits = deposits.filter(deposit => deposit.status?.id === "1");

	if (activeDeposits.length === 0) {
		return null;
	}

	let bestMatch: { deposit: DepositBackendResponse; rule: DepositRuleBackendResponse; thresholdValue: number } | null = null;

	// Iterate through all active deposits and their rules
	for (const deposit of activeDeposits) {
		// Check if deposit has user-specific rules
		if (deposit.user && userId) {
			// This deposit is user-specific
			if (deposit.user.id !== userId) {
				// Skip if not matching the user
				continue;
			}
		}

		// Check deposit rules
		if (!deposit.depositRules || deposit.depositRules.length === 0) {
			continue;
		}

		for (const rule of deposit.depositRules) {
			// Check if the rule threshold matches the amount
			if (matchesThreshold(totalAmount, rule.threshold)) {
				const { value: thresholdValue } = parseThreshold(rule.threshold);

				// Keep the rule with the highest matching threshold
				if (!bestMatch || thresholdValue > bestMatch.thresholdValue) {
					bestMatch = { deposit, rule, thresholdValue };
				}
			}
		}
	}

	if (!bestMatch) {
		return null;
	}

	// Calculate deposit amount
	const depositAmount = calculateDepositAmount(totalAmount, bestMatch.rule);
	const remainingAmount = totalAmount - depositAmount;

	return {
		rule: bestMatch.rule,
		deposit: bestMatch.deposit,
		depositAmount: Math.round(depositAmount * 100) / 100, // Round to 2 decimal places
		remainingAmount: Math.round(remainingAmount * 100) / 100,
	};
};

/**
 * Format deposit rule display text
 */
export const formatDepositRuleText = (rule: DepositRuleBackendResponse): string => {
	const ruleTypeId = parseInt(rule.type.id);
	
	if (ruleTypeId === DepositRuleTypeEnum.Percentage) {
		return `${rule.amount}% deposit required for ${rule.threshold}`;
	} else if (ruleTypeId === DepositRuleTypeEnum.Fixed_Amount) {
		return `$${rule.amount} deposit required for ${rule.threshold}`;
	}

	return "";
};

/**
 * Check if deposit has exceptions that might apply
 */
export const checkDepositExceptions = (
	deposit: DepositBackendResponse,
	context?: any
): boolean => {
	if (!deposit.depositExceptions || deposit.depositExceptions.length === 0) {
		return false;
	}

	// Add logic here to check if any exceptions apply based on context
	// For now, return false (no exceptions apply)
	return false;
};

